import React, { useState, useEffect, useCallback, useRef } from 'react';
import AdminLayout from '../../../components/admin/AdminLayout';
import CmsTabs from '../../../components/admin/CmsTabs';
import RichTextEditor from '../../../components/admin/cms/RichTextEditor';
import { formatApiError } from '../../../lib/apiError';
import { useToast } from '../../../hooks/use-toast';
import axios from 'axios';
import {
  Layers, Plus, Pencil, Trash2, Loader2, X, AlertCircle, Copy,
  ToggleLeft, ToggleRight, ArrowUp, ArrowDown, Search, ChevronLeft, ChevronRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-slate-500/20 text-slate-400 border-slate-500/30' },
  published: { label: 'Enabled', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  archived: { label: 'Archived', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
};

const SectionModal = ({ isOpen, onClose, pageId, section, onSave }) => {
  const [form, setForm] = useState({ slug: '', title: '', type: 'generic', visibility: 'private', order: 0, locale: 'en' });
  const [bodyHtml, setBodyHtml] = useState('');
  const [editorKey, setEditorKey] = useState(0);
  const contentRef = useRef({});
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (section) {
      setForm({
        slug: section.slug, title: section.title, type: section.type, visibility: section.visibility,
        order: section.order, locale: section.locale,
      });
      contentRef.current = section.content || {};
      setBodyHtml((section.content && section.content.body_html) || '');
    } else {
      setForm({ slug: '', title: '', type: 'generic', visibility: 'private', order: 0, locale: 'en' });
      contentRef.current = {};
      setBodyHtml('');
    }
    setEditorKey((k) => k + 1);
    setError('');
  }, [section, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSaving(true);
    const payload = {
      slug: form.slug, title: form.title, type: form.type, visibility: form.visibility,
      order: Number(form.order) || 0, locale: form.locale,
      content: { ...contentRef.current, body_html: bodyHtml },
    };
    if (section) payload.expected_updated_at = section.updated_at;
    try {
      if (section) await axios.put(`${API_URL}/api/admin/cms/sections/${section.id}`, payload);
      else await axios.post(`${API_URL}/api/admin/cms/pages/${pageId}/sections`, payload);
      onSave(); onClose();
    } catch (err) {
      setError(formatApiError(err, 'Failed to save section'));
    } finally { setSaving(false); }
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-slate-800 border border-slate-700 rounded-xl w-full max-w-xl mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-700 sticky top-0 bg-slate-800">
          <h2 className="text-xl font-semibold text-white">{section ? 'Edit Section' : 'Create Section'}</h2>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4" data-testid="cms-section-form">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm" data-testid="cms-section-form-error">
              <AlertCircle className="w-4 h-4" />{error}
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Title *</label>
              <input type="text" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                data-testid="cms-section-title-input"
                className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-amber-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Slug *</label>
              <input type="text" required value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })}
                placeholder="hero-section" data-testid="cms-section-slug-input"
                className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-amber-500 font-mono text-sm" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Type</label>
              <input type="text" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
                data-testid="cms-section-type-input"
                className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-amber-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Visibility</label>
              <select value={form.visibility} onChange={(e) => setForm({ ...form, visibility: e.target.value })}
                data-testid="cms-section-visibility-select"
                className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-amber-500">
                <option value="private">Private</option>
                <option value="public">Public</option>
                <option value="hidden">Hidden</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Order</label>
              <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })}
                data-testid="cms-section-order-input"
                className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-amber-500" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Content</label>
            <RichTextEditor key={editorKey} value={bodyHtml} onChange={setBodyHtml} />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-slate-400 hover:text-white transition-colors">Cancel</button>
            <button type="submit" disabled={saving} data-testid="cms-section-save-btn"
              className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-slate-900 font-semibold rounded-lg hover:bg-amber-400 disabled:opacity-50 transition-colors">
              {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : <>{section ? 'Update' : 'Create'} Section</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CmsSections = () => {
  const { toast } = useToast();
  const [allPages, setAllPages] = useState([]);
  const [selectedPageId, setSelectedPageId] = useState('');
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [search, setSearch] = useState('');
  const [pageNum, setPageNum] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/cms/pages`)
      .then((r) => {
        setAllPages(r.data);
        if (r.data.length && !selectedPageId) setSelectedPageId(r.data[0].id);
      })
      .catch((e) => console.error('Error fetching pages:', e));
  }, []); // eslint-disable-line

  const fetchSections = useCallback(async () => {
    if (!selectedPageId) { setSections([]); return; }
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/cms/pages/${selectedPageId}/sections`);
      setSections(r.data);
    } catch (e) { console.error('Error fetching sections:', e); }
    finally { setLoading(false); }
  }, [selectedPageId]);

  useEffect(() => { fetchSections(); }, [fetchSections]);

  const handleAction = async (fn, id, successMsg) => {
    setBusyId(id);
    try {
      await fn();
      await fetchSections();
      if (successMsg) toast({ title: successMsg });
    } catch (e) {
      toast({ title: 'Action failed', description: formatApiError(e, 'Action failed'), variant: 'destructive' });
    } finally { setBusyId(null); }
  };

  const doEnable = (s) => handleAction(() => axios.post(`${API_URL}/api/admin/cms/sections/${s.id}/publish`), s.id, 'Section enabled');
  const doDisable = (s) => handleAction(() => axios.post(`${API_URL}/api/admin/cms/sections/${s.id}/unpublish`), s.id, 'Section disabled');
  const doDuplicate = (s) => handleAction(() => axios.post(`${API_URL}/api/admin/cms/sections/${s.id}/duplicate`), s.id, 'Section duplicated');
  const doDelete = (s) => {
    if (!window.confirm(`Delete section "${s.title}"?`)) return;
    handleAction(() => axios.delete(`${API_URL}/api/admin/cms/sections/${s.id}`), s.id, 'Section deleted');
  };

  // ordering: swap order value with neighbor, then persist via reorder
  const ordered = [...sections].sort((a, b) => a.order - b.order);
  const move = async (s, dir) => {
    const idx = ordered.findIndex((x) => x.id === s.id);
    const swapIdx = dir === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= ordered.length) return;
    const other = ordered[swapIdx];
    await handleAction(() => axios.post(`${API_URL}/api/admin/cms/sections/reorder`, {
      items: [{ id: s.id, order: other.order }, { id: other.id, order: s.order }],
    }), s.id);
  };

  const formatDate = (d) => (d ? new Date(d).toLocaleString() : '-');

  const filtered = ordered.filter((s) => !search || s.title.toLowerCase().includes(search.toLowerCase()) || s.slug.toLowerCase().includes(search.toLowerCase()));
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const current = Math.min(pageNum, totalPages);
  const paginated = filtered.slice((current - 1) * pageSize, current * pageSize);
  useEffect(() => { setPageNum(1); }, [search, selectedPageId]);

  return (
    <AdminLayout>
      <SectionModal isOpen={showModal} onClose={() => setShowModal(false)} pageId={selectedPageId} section={editing} onSave={fetchSections} />

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white mb-2 flex items-center gap-2" data-testid="cms-sections-title">
          <Layers className="w-6 h-6 text-amber-400" /> CMS
        </h1>
        <p className="text-slate-400">Manage the sections that belong to each page.</p>
      </div>

      <CmsTabs />

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <label className="text-sm text-slate-400">Page:</label>
          <select value={selectedPageId} onChange={(e) => setSelectedPageId(e.target.value)} data-testid="cms-sections-page-select"
            className="px-3 py-2.5 bg-slate-900/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-amber-500 min-w-[240px]">
            {allPages.length === 0 && <option value="">No pages available</option>}
            {allPages.map((p) => <option key={p.id} value={p.id}>{p.title} (/{p.slug})</option>)}
          </select>
        </div>
        <button onClick={() => { setEditing(null); setShowModal(true); }} disabled={!selectedPageId} data-testid="cms-create-section-btn"
          className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-slate-900 font-semibold rounded-lg hover:bg-amber-400 disabled:opacity-50 transition-colors">
          <Plus className="w-5 h-5" /> Create Section
        </button>
      </div>

      <div className="relative mb-4 max-w-md">
        <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
        <input type="text" placeholder="Search sections..." value={search} onChange={(e) => setSearch(e.target.value)}
          data-testid="cms-sections-search"
          className="w-full pl-9 pr-4 py-2.5 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-amber-500" />
      </div>

      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-amber-500" /></div>
        ) : paginated.length === 0 ? (
          <div className="text-center py-12" data-testid="cms-sections-empty">
            <Layers className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">{selectedPageId ? 'No sections for this page' : 'Select a page to view its sections'}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="cms-sections-table">
              <thead>
                <tr className="border-b border-slate-700">
                  {['Order', 'Title', 'Type', 'Status', 'Updated', 'Actions'].map((h) => (
                    <th key={h} className={`text-xs font-medium text-slate-400 uppercase tracking-wider px-5 py-4 ${h === 'Actions' ? 'text-right' : 'text-left'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {paginated.map((s, i) => (
                  <tr key={s.id} className="hover:bg-slate-700/20 transition-colors" data-testid={`cms-section-row-${s.id}`}>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-1">
                        <span className="text-slate-300 text-sm w-6">{s.order}</span>
                        <div className="flex flex-col">
                          <button onClick={() => move(s, 'up')} title="Move up" data-testid={`cms-section-up-${s.id}`}
                            className="text-slate-500 hover:text-amber-400 transition-colors"><ArrowUp className="w-3.5 h-3.5" /></button>
                          <button onClick={() => move(s, 'down')} title="Move down" data-testid={`cms-section-down-${s.id}`}
                            className="text-slate-500 hover:text-amber-400 transition-colors"><ArrowDown className="w-3.5 h-3.5" /></button>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <span className="font-medium text-white">{s.title}</span>
                      <p className="text-xs text-slate-500 font-mono mt-1">{s.slug}</p>
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-400">{s.type}</td>
                    <td className="px-5 py-4">
                      <span className={`inline-block px-2 py-1 text-xs font-medium rounded border ${STATUS_CONFIG[s.status]?.color}`} data-testid={`cms-section-status-${s.id}`}>
                        {STATUS_CONFIG[s.status]?.label || s.status}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-400">{formatDate(s.updated_at)}</td>
                    <td className="px-5 py-4 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        {busyId === s.id && <Loader2 className="w-4 h-4 animate-spin text-amber-400" />}
                        <button onClick={() => { setEditing(s); setShowModal(true); }} title="Edit" data-testid={`cms-section-edit-${s.id}`}
                          className="p-2 bg-slate-700/50 text-slate-300 rounded-lg hover:bg-slate-700 transition-colors"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => doDuplicate(s)} title="Duplicate" data-testid={`cms-section-duplicate-${s.id}`}
                          className="p-2 bg-slate-700/50 text-slate-300 rounded-lg hover:bg-slate-700 transition-colors"><Copy className="w-4 h-4" /></button>
                        {s.status !== 'published' ? (
                          <button onClick={() => doEnable(s)} title="Enable" data-testid={`cms-section-enable-${s.id}`}
                            className="p-2 bg-emerald-500/20 text-emerald-400 rounded-lg hover:bg-emerald-500/30 transition-colors"><ToggleRight className="w-4 h-4" /></button>
                        ) : (
                          <button onClick={() => doDisable(s)} title="Disable" data-testid={`cms-section-disable-${s.id}`}
                            className="p-2 bg-slate-600/40 text-slate-300 rounded-lg hover:bg-slate-600/60 transition-colors"><ToggleLeft className="w-4 h-4" /></button>
                        )}
                        <button onClick={() => doDelete(s)} title="Delete" data-testid={`cms-section-delete-${s.id}`}
                          className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!loading && filtered.length > 0 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-slate-400" data-testid="cms-sections-count">
            Showing {(current - 1) * pageSize + 1}-{Math.min(current * pageSize, filtered.length)} of {filtered.length}
          </p>
          <div className="flex items-center gap-2">
            <button disabled={current <= 1} onClick={() => setPageNum(current - 1)} data-testid="cms-sections-prev"
              className="p-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-300 hover:bg-slate-700 disabled:opacity-40 transition-colors"><ChevronLeft className="w-4 h-4" /></button>
            <span className="text-sm text-slate-300">Page {current} / {totalPages}</span>
            <button disabled={current >= totalPages} onClick={() => setPageNum(current + 1)} data-testid="cms-sections-next"
              className="p-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-300 hover:bg-slate-700 disabled:opacity-40 transition-colors"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default CmsSections;
